import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [manifestors, setManifestors] = useState([]);
  const [activity, setActivity] = useState(null);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    if (!token || !user?.is_admin) { navigate('/'); return; }
    loadAdmin();
  }, [token, user]);

  const headers = { Authorization: `Bearer ${token}` };

  const loadAdmin = async () => {
    try {
      const [sRes, mRes, aRes] = await Promise.all([
        fetch(`${API}/api/admin/stats`, { headers }),
        fetch(`${API}/api/admin/manifestors`, { headers }),
        fetch(`${API}/api/admin/recent-activity`, { headers }),
      ]);
      if (sRes.ok) setStats(await sRes.json());
      if (mRes.ok) setManifestors(await mRes.json());
      if (aRes.ok) setActivity(await aRes.json());
    } catch { /* silent */ }
  };

  if (!user?.is_admin) return null;

  return (
    <section data-testid="admin-page" className="admin-page">
      <nav className="profile-nav">
        <Link to="/profile" className="path-back-link">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
          Back to Profile
        </Link>
      </nav>

      <div className="admin-header">
        <h1 className="admin-title">Village Admin</h1>
        <p className="admin-subtitle">See who's walking the paths</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="admin-stats" data-testid="admin-stats">
          <div className="stat-card">
            <span className="stat-number">{stats.total_manifestors}</span>
            <span className="stat-label">Manifestors</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{stats.total_journals}</span>
            <span className="stat-label">Journal Entries</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{stats.total_quotes}</span>
            <span className="stat-label">Affirmations</span>
          </div>
          <div className="stat-card">
            <span className="stat-number">{stats.total_email_subscribers}</span>
            <span className="stat-label">Email Subscribers</span>
          </div>
        </div>
      )}

      {/* Archetype Distribution */}
      {stats?.archetype_distribution && Object.keys(stats.archetype_distribution).length > 0 && (
        <div className="admin-section">
          <h2 className="admin-section-title">Archetype Distribution</h2>
          <div className="archetype-bars">
            {Object.entries(stats.archetype_distribution).map(([key, count]) => (
              <div key={key} className="archetype-bar-row">
                <span className="archetype-bar-label">{key}</span>
                <div className="archetype-bar-track">
                  <div className="archetype-bar-fill" style={{ width: `${(count / stats.total_manifestors) * 100}%` }} />
                </div>
                <span className="archetype-bar-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manifestors List */}
      <div className="admin-section">
        <h2 className="admin-section-title">All Manifestors ({manifestors.length})</h2>
        <div className="admin-table-wrap">
          <table className="admin-table" data-testid="admin-manifestors-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Archetype</th>
                <th>Paths</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {manifestors.map((m) => (
                <tr key={m.id}>
                  <td>{m.name}</td>
                  <td>{m.email}</td>
                  <td>{m.archetype_name || '—'}</td>
                  <td>{m.paths_visited?.length || 0}/6</td>
                  <td>{m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Activity */}
      {activity && (
        <div className="admin-section">
          <h2 className="admin-section-title">Recent Journals</h2>
          {activity.recent_journals.length === 0 && <p className="empty-state">No journal entries yet.</p>}
          {activity.recent_journals.map((j, i) => (
            <div key={i} className="admin-journal-card">
              <h4>{j.title}</h4>
              <p>{j.content?.substring(0, 200)}{j.content?.length > 200 ? '...' : ''}</p>
              <span className="journal-entry-date">{new Date(j.created_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
